import { Box } from "@mui/material";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocaleSelector } from "src/domains/shared/components/LocaleSelector";
import {
  SITE_HEADER_HEIGHT_PX,
  SITE_HEADER_HIDDEN_ATTR,
} from "src/domains/shared/constants/styles/site";
import { siteFileUrl } from "../../siteKit/media/siteMedia";
import { useScrollFrame } from "../../siteKit/motion/scrollFrame";
import { DroneSquareMark } from "../DroneSquareMark";
import {
  AccountActions,
  MobileAccountItems,
  useSiteAccount,
} from "./AccountActions";
import { ShareMenuItem, SharePageButton } from "./SharePageButton";
import { SITE_HEADER_STYLES as S } from "./siteHeaderStyles";
import {
  SITE_NAV_LINKS,
  SITE_PAGE_KEY_BY_ROUTE,
  SITE_SERVICE_LINKS,
} from "./siteNav";

type OpenMenu = "services" | "mobile" | null;

/** 이만큼 스크롤되면 머리말 아래 그림자를 단다 */
const SCROLLED_THRESHOLD_PX = 8;
/** 머리말 높이보다 더 내려간 뒤에만 숨는다 — 맨 위 근처에서는 늘 보인다 */
const HIDE_BELOW_PX = SITE_HEADER_HEIGHT_PX;

const CHEVRON_DOWN_PATH = "M6 8l4 4 4-4";
const BURGER_PATH = "M3 6h18M3 12h18M3 18h18";

/**
 * 사이트 머리말(marketing-site-shell): 왼쪽 로고, 가운데 메뉴(서비스 패널 포함), 오른쪽 공유·언어·계정.
 * 900px 이하에서는 짧은 언어 코드·가운데 로고·메뉴 버튼으로 바뀌고 「공유하기」 는 메뉴 목록 안에 있다. 스크롤 그림자와
 * 숨김(아래로 내리면 숨고 올리면 나타남, 메뉴가 열려 있으면 유지)은 프레임당 한 번 DOM 속성으로 단다.
 */
export function SiteHeader() {
  const { t } = useTranslation("landing");
  const router = useRouter();
  const currentPage = SITE_PAGE_KEY_BY_ROUTE[router.pathname];
  const account = useSiteAccount();
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const headerRef = useRef<HTMLElement>(null);
  const serviceHolderRef = useRef<HTMLDivElement>(null);
  const serviceButtonRef = useRef<HTMLButtonElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLElement>(null);
  const servicePanelId = useId();
  const mobileMenuId = useId();
  /** 지난 프레임의 scrollY — 처음(복원된 위치 포함)은 방향이 없어 보이는 채로 시작한다 */
  const lastScrollYRef = useRef<number | null>(null);

  useScrollFrame((scrollY) => {
    const header = headerRef.current;
    if (!header) return;
    header.toggleAttribute("data-scrolled", scrollY > SCROLLED_THRESHOLD_PX);
    const scrollingDown =
      lastScrollYRef.current !== null && scrollY > lastScrollYRef.current;
    lastScrollYRef.current = scrollY;
    const hidden =
      scrollingDown && scrollY > HIDE_BELOW_PX && openMenu === null;
    header.toggleAttribute("data-hidden", hidden);
    // 머리말 아래에 붙는 틀(홈 첫 장면)이 머리말 자리까지 차오르도록 문서에도 알린다
    document.documentElement.toggleAttribute(SITE_HEADER_HIDDEN_ATTR, hidden);
  });
  useEffect(
    () => () => {
      document.documentElement.removeAttribute(SITE_HEADER_HIDDEN_ATTR);
    },
    []
  );

  useEffect(() => {
    if (!openMenu) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const trigger =
        openMenu === "services" ? serviceButtonRef.current : burgerRef.current;
      setOpenMenu(null);
      trigger?.focus();
    };
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const inside = [serviceHolderRef, mobileMenuRef, burgerRef].some((ref) =>
        ref.current?.contains(target)
      );
      if (!inside) setOpenMenu(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClick);
    };
  }, [openMenu]);

  const toggle = (menu: Exclude<OpenMenu, null>) =>
    setOpenMenu((open) => (open === menu ? null : menu));
  const closeMenus = () => setOpenMenu(null);
  const currentAttr = (pageKey: string) =>
    pageKey === currentPage ? ("page" as const) : undefined;
  const isServicePage = SITE_SERVICE_LINKS.some(
    (link) => link.pageKey === currentPage
  );

  const releasesDot = (dotSx: typeof S.dot | typeof S.mobileDot) => (
    <Box component="i" aria-hidden="true" sx={dotSx} />
  );

  return (
    <>
      <Box component="a" href="#main" sx={S.skip}>
        {t("site.skipToContent")}
      </Box>
      <Box component="header" ref={headerRef} sx={S.header}>
        <Box sx={S.inner}>
          {/* 좁은 화면 순서: 언어 → 로고 → 메뉴 버튼(보이는 순서 = 초점 순서). 넓은 화면에서는 둘 다 숨는다 */}
          <Box sx={S.mobileLocale}>
            <LocaleSelector variant="site" isMobile />
          </Box>
          <Box
            component={Link}
            href="/"
            sx={S.logo}
            aria-label={t("site.homeLabel")}
          >
            <DroneSquareMark />
            <span>DroneSquare</span>
          </Box>
          <Box component="nav" sx={S.nav} aria-label={t("site.nav.label")}>
            <Box ref={serviceHolderRef} sx={S.serviceHolder}>
              <Box
                component="button"
                type="button"
                ref={serviceButtonRef}
                sx={S.navLink}
                data-current={isServicePage ? "" : undefined}
                aria-expanded={openMenu === "services"}
                aria-controls={servicePanelId}
                onClick={() => toggle("services")}
              >
                {t("site.nav.services")}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    d={CHEVRON_DOWN_PATH}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
              <Box
                id={servicePanelId}
                sx={S.servicePanel}
                hidden={openMenu !== "services"}
              >
                {SITE_SERVICE_LINKS.map((link) => (
                  <Box
                    key={link.href}
                    component={Link}
                    href={link.href}
                    sx={S.serviceCard}
                    aria-current={currentAttr(link.pageKey)}
                    onClick={closeMenus}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- 사이트 미디어는 파이프라인이 만든 해시 파일을 그대로 쓴다(next/image 최적화 대상 아님) */}
                    <img
                      src={siteFileUrl(link.art)}
                      alt=""
                      width={96}
                      height={96}
                      loading="lazy"
                      decoding="async"
                    />
                    <b>{t(`site.servicePanel.${link.labelKey}.title`)}</b>
                    <small>
                      {t(`site.servicePanel.${link.labelKey}.description`)}
                    </small>
                    <i>{t("site.servicePanel.more")}</i>
                  </Box>
                ))}
              </Box>
            </Box>
            {SITE_NAV_LINKS.map((link) => (
              <Box
                key={link.href}
                component={Link}
                href={link.href}
                sx={S.navLink}
                aria-current={currentAttr(link.pageKey)}
              >
                {t(`site.nav.${link.labelKey}`)}
                {link.pageKey === "releases" && releasesDot(S.dot)}
              </Box>
            ))}
          </Box>
          <Box sx={S.right}>
            <SharePageButton />
            <LocaleSelector variant="site" />
            <AccountActions account={account} />
          </Box>
          <Box
            component="button"
            type="button"
            ref={burgerRef}
            sx={S.burger}
            aria-label={t(
              openMenu === "mobile" ? "site.nav.closeMenu" : "site.nav.openMenu"
            )}
            aria-expanded={openMenu === "mobile"}
            aria-controls={mobileMenuId}
            onClick={() => toggle("mobile")}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d={BURGER_PATH}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Box>
        </Box>
        <Box
          component="nav"
          id={mobileMenuId}
          ref={mobileMenuRef}
          sx={S.mobileMenu}
          aria-label={t("site.nav.mobileLabel")}
          hidden={openMenu !== "mobile"}
        >
          {SITE_SERVICE_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              aria-current={currentAttr(link.pageKey)}
              onClick={closeMenus}
            >
              {t(`site.servicePanel.${link.labelKey}.title`)}
            </Link>
          ))}
          {SITE_NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              aria-current={currentAttr(link.pageKey)}
              onClick={closeMenus}
            >
              {t(`site.nav.${link.labelKey}`)}
              {link.pageKey === "releases" && releasesDot(S.mobileDot)}
            </Link>
          ))}
          {/* 「공유하기」 는 로그인 상태 항목 바로 위 — 머리말에는 공유가 없다 */}
          <ShareMenuItem />
          <MobileAccountItems account={account} onNavigate={closeMenus} />
        </Box>
      </Box>
    </>
  );
}
